import {HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from "@angular/common/http";
import {Injectable, Injector, OnDestroy} from "@angular/core";
import {Observable} from "rxjs/Observable";
import {Subject} from "rxjs";
import {FilterBuilder, FilterCriteria} from "@synisys/idm-de-core-frontend";
import {ApplicationPropertiesService} from "@synisys/idm-application-properties-service-client-js";
import {fromPromise} from "rxjs/observable/fromPromise";
import {catchError, map, shareReplay, switchMap} from "rxjs/operators";
import {of} from "rxjs/observable/of";
import {Tree} from "./me-tree-shaking-interceptor";

@Injectable()
export class PortfolioInterceptor implements HttpInterceptor, OnDestroy {

	public applicationProperties: ApplicationPropertiesService;
	private httpClient: HttpClient;
	private agraServiceUrl$: Observable<string>;
	private authServiceUrl$: Observable<string>;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    constructor(private injector: Injector) {

    }

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		!this.httpClient && this.injectDependencies();
		if (!this.isNeededRequest(req)) {
			return next.handle(req);
		}
		return this.isUserFromGranteeOrg().pipe(
			switchMap((needToIntercept) => {
				if (needToIntercept) {
					return this.applyFilter(req, next).pipe(
						switchMap((response: HttpResponse<Tree>) => of(response))
					);
				} else {
					return next.handle(req);
				}
			}),
			catchError((error) => {
				console.error(error);
				return next.handle(req);
			})
		);
	}

	private applyFilter(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		req.body.filter = this.defineFilter(req);
		return next.handle(req);
	}

	private defineFilter(req: HttpRequest<any>): any {
		if (!Object.keys(req.body.filter).length) {
			return this.getExtraFilter();
		} else {
			let filterStr: string = JSON.stringify(req.body.filter);
			if (filterStr.indexOf('wfstateid') === -1) {
				let extraFilter: string = JSON.stringify(this.getExtraFilter());
				let moreThenOneFieldFilter: boolean = filterStr.indexOf('$and') !== -1;
				let newFilterStr: string;
				if (moreThenOneFieldFilter) {
					newFilterStr = filterStr.substring(0, filterStr.length - 2) + ', ' + extraFilter +
						filterStr.substring(filterStr.length - 2);
				} else {
					newFilterStr = `{"$and": [` + filterStr + ', ' + extraFilter + ']}'
				}
				return JSON.parse(newFilterStr);
			} else {
				let closedStateFilter: string = '{"wfstateid":29}'
				if (filterStr.indexOf(closedStateFilter) !== -1) {
					let extraFilter: string = '{"instanceId":0}';
					let newFilterStr: string = filterStr.replace(closedStateFilter, extraFilter)
					return JSON.parse(newFilterStr);
				} else {
					return req.body.filter;
				}
			}
		}
	}

	private isNeededRequest(req: HttpRequest<any>): boolean {
		return req.method === 'POST' && req.url.indexOf('/entitysearchservice/v1/agra/portfolio/entities') !== -1 &&
			req.body.category === 'Request';
	}

	private getExtraFilter(): any {
		let filterBuilder: FilterBuilder = new FilterBuilder();
		let filterCriteria: FilterCriteria = filterBuilder.not(new FilterBuilder().is('wfstateid', 29).build()).build();
		return filterCriteria.toJson();
	}

	private isUserFromGranteeOrg(): Observable<boolean> {
		return this.authServiceUrl$.pipe(
			switchMap((url) => this.httpClient.get<{ userId: { userData: any } }>(`${url}/me`)),
			map((response) => response.userId),
			switchMap((userId) =>
				this.agraServiceUrl$.pipe(
					switchMap((url) => this.httpClient.get<{ data: any }>(`${url}/common/user/organization/${userId}`)),
					map((orgId) => orgId)
				)
			),
			switchMap(this.checkOrganization.bind(this))
		);
	}

	private checkOrganization(organizationId: number): Observable<boolean> {
		return Observable.of(organizationId === 2);
	}

	private injectDependencies(): void {
		this.httpClient = this.injector.get(HttpClient);
		this.applicationProperties = this.injector.get(ApplicationPropertiesService);
		this.agraServiceUrl$ = fromPromise(this.applicationProperties.getProperty('agra-service-url')).pipe(shareReplay());
		this.authServiceUrl$ = fromPromise(this.applicationProperties.getProperty('auth-service-url')).pipe(shareReplay());
	}

}
